import { createReducer } from '@reduxjs/toolkit'
import {
  UserActions,
  Resource,
  UserCompany,
  Accountant,
  UserDetails,
  UserSubscription,
  BankAccount,
  Preferences,
} from 'shared/types'
import {
  initResource,
  setFailed,
  setPending,
  setSucceeded,
  convertObjectNullValuesToEmptyString,
} from 'shared/utils'
import * as actions from './actions'

interface State {
  userActions: Resource<UserActions>
  userDetails: Resource<UserDetails>
  assignAccountant: Resource
  deleteAccountant: Resource
  accountant: Resource<Accountant | null>
  companyData: Resource<UserCompany | null>
  editCompany: Resource
  userConfig: Resource<UserCompany | null>
  getUserConfig: Resource
  currentSubscription: Resource<UserSubscription>
  bankAccounts: Resource<BankAccount[] | null>
  createBankAccount: Resource
  deleteBankAccount: Resource
  notificationSettings: Resource<Preferences | null>
}

const initialState: State = {
  userActions: initResource({
    isContractorDataIncomplete: false,
    isCompanyDataIncomplete: false,
    isRegistrationComplete: true,
    isPowerOfAttorneyUploaded: true,
    isUserDeletable: true,
    lastImportDate: null,
  }),
  userDetails: initResource({
    invoices: null,
    subscription: null,
    lawsuits: null,
    lastImport: null,
  }),
  assignAccountant: initResource(),
  deleteAccountant: initResource(),
  accountant: initResource(null),
  editCompany: initResource(),
  companyData: initResource(null),
  userConfig: initResource(null),
  getUserConfig: initResource(),
  currentSubscription: initResource({
    smsNotificationsUsage: null,
    subscription: null,
  }),
  bankAccounts: initResource(),
  createBankAccount: initResource(),
  deleteBankAccount: initResource(),
  notificationSettings: initResource(),
}

export default createReducer(initialState, builder =>
  builder
    .addCase(actions.getUserActions.pending, state => {
      setPending(state.userActions)
    })
    .addCase(actions.getUserActions.fulfilled, (state, { payload }) => {
      setSucceeded(state.userActions, payload)
    })
    .addCase(actions.getUserActions.rejected, (state, { payload }) => {
      setFailed(state.userActions, payload)
    })
    .addCase(actions.getUserDetails.pending, state => {
      setPending(state.userDetails)
    })
    .addCase(actions.getUserDetails.fulfilled, (state, { payload }) => {
      setSucceeded(state.userDetails, payload)
    })
    .addCase(actions.getUserDetails.rejected, (state, { payload }) => {
      setFailed(state.userDetails, payload)
    })
    .addCase(actions.assignAccountant.pending, state => {
      setPending(state.assignAccountant)
    })
    .addCase(actions.assignAccountant.fulfilled, (state, { payload }) => {
      setSucceeded(state.assignAccountant)
      state.accountant.data = payload
    })
    .addCase(actions.assignAccountant.rejected, (state, { payload }) => {
      setFailed(state.assignAccountant, payload)
    })
    .addCase(actions.getAccountant.pending, state => {
      setPending(state.accountant)
    })
    .addCase(actions.getAccountant.fulfilled, (state, { payload }) => {
      setSucceeded(state.accountant, payload)
    })
    .addCase(actions.getAccountant.rejected, (state, { payload }) => {
      setFailed(state.accountant, payload)
    })
    .addCase(actions.deleteAccountant.pending, state => {
      setPending(state.deleteAccountant)
    })
    .addCase(actions.deleteAccountant.fulfilled, (state, { payload }) => {
      setSucceeded(state.deleteAccountant)
      state.accountant.data = null
    })
    .addCase(actions.deleteAccountant.rejected, (state, { payload }) => {
      setFailed(state.deleteAccountant, payload)
    })
    .addCase(actions.getCompanyInfo.pending, state => {
      setPending(state.companyData)
    })
    .addCase(actions.getCompanyInfo.fulfilled, (state, { payload }) => {
      setSucceeded(state.companyData, payload)
    })
    .addCase(actions.getCompanyInfo.rejected, state => {
      setFailed(state.companyData)
    })
    .addCase(actions.editCompanyData.pending, state => {
      setPending(state.editCompany)
    })
    .addCase(actions.editCompanyData.fulfilled, (state, { payload }) => {
      setSucceeded(state.editCompany)
    })
    .addCase(actions.editCompanyData.rejected, state => {
      setFailed(state.editCompany)
    })
    .addCase(actions.addUserConfig.pending, state => {
      setPending(state.userConfig)
    })
    .addCase(
      actions.addUserConfig.fulfilled,
      (state, { meta: { arg: companyData } }) => {
        setSucceeded(state.userConfig, companyData)
      }
    )
    .addCase(actions.addUserConfig.rejected, (state, action) => {
      setFailed(state.userConfig, action.error.code)
    })
    .addCase(actions.editUserConfig.pending, state => {
      setPending(state.userConfig)
    })
    .addCase(
      actions.editUserConfig.fulfilled,
      (state, { meta: { arg: companyData } }) => {
        setSucceeded(state.userConfig, companyData)
      }
    )
    .addCase(actions.editUserConfig.rejected, (state, action) => {
      setFailed(state.userConfig, action.error.code)
    })
    .addCase(actions.getUserConfig.pending, state => {
      setPending(state.getUserConfig)
    })
    .addCase(actions.getUserConfig.fulfilled, (state, { payload }) => {
      setSucceeded(state.getUserConfig)
      state.userConfig.data = convertObjectNullValuesToEmptyString(payload)
    })
    .addCase(actions.getUserConfig.rejected, (state, action) => {
      setFailed(state.getUserConfig, action.error.code)
    })
    .addCase(actions.getCurrentSubscription.pending, state => {
      setPending(state.currentSubscription)
    })
    .addCase(actions.getCurrentSubscription.fulfilled, (state, { payload }) => {
      setSucceeded(state.currentSubscription, payload)
    })
    .addCase(actions.getCurrentSubscription.rejected, (state, action) => {
      setFailed(state.currentSubscription, action.error.code)
    })
    .addCase(actions.setNoSubscription, state => {
      setSucceeded(state.currentSubscription, null)
    })
    .addCase(actions.getBankAccounts.pending, state => {
      setPending(state.bankAccounts)
    })
    .addCase(actions.getBankAccounts.fulfilled, (state, { payload }) => {
      setSucceeded(state.bankAccounts, payload)
    })
    .addCase(actions.getBankAccounts.rejected, (state, action) => {
      setFailed(state.bankAccounts, action.error.code)
    })
    .addCase(actions.createBankAccount.pending, state =>
      setPending(state.createBankAccount)
    )
    .addCase(actions.createBankAccount.fulfilled, state =>
      setSucceeded(state.createBankAccount)
    )
    .addCase(actions.createBankAccount.rejected, (state, action) =>
      setFailed(state.createBankAccount, action.error.code)
    )
    .addCase(actions.getNotificationSettings.pending, state =>
      setPending(state.notificationSettings)
    )
    .addCase(actions.getNotificationSettings.fulfilled, (state, { payload }) =>
      setSucceeded(state.notificationSettings, payload)
    )
    .addCase(actions.getNotificationSettings.rejected, (state, action) =>
      setFailed(state.notificationSettings, action.error.code)
    )
    .addCase(actions.updateNotificationSettings.pending, state =>
      setPending(state.notificationSettings)
    )
    .addCase(actions.updateNotificationSettings.fulfilled, state =>
      setSucceeded(state.notificationSettings)
    )
    .addCase(actions.updateNotificationSettings.rejected, (state, action) =>
      setFailed(state.notificationSettings, action.error.code)
    )
    .addCase(actions.clearNotificationData, state => {
      state.notificationSettings.data = null
    })
)
